import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { NotFoundInPage } from '@/components/NotFoundInPage';
import type { AppealSent, SentStatus } from '../types';
import { APPEALS_USER, money } from '../utils';
import { isSentDecided } from '../store';
import { Check } from './common';
import { CardRow } from './CardRow';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const BADGE: Record<SentStatus, { label: string; cls: string }> = {
  sent: { label: 'Sent, not confirmed', cls: 'b-mute' },
  faxed: { label: 'Faxed, not confirmed', cls: 'b-mute' },
  in_mail: { label: 'In the mail', cls: 'b-mute' },
  waiting: { label: 'Payer has it', cls: 'b-info' },
  reprocessing: { label: 'Reprocessing', cls: 'b-info' },
  returned: { label: 'Returned', cls: 'b-warn' },
  didnt_go: { label: 'Didn’t go through', cls: 'b-warn' },
  partly_paid: { label: 'Partly paid', cls: 'b-warn' },
  overdue: { label: 'No answer yet', cls: 'b-warn' },
  denied_again: { label: 'Denied again', cls: 'b-bad' },
  paid: { label: 'Paid', cls: 'b-ok' },
};

// D131 — sent is not received. "Sent, not confirmed yet" is everything that went
// out but the payer hasn't acknowledged; "Payer has it, waiting" is confirmed and
// awaiting an answer. The two counts never share a row.
const NOT_CONFIRMED: SentStatus[] = ['sent', 'faxed', 'in_mail', 'returned', 'didnt_go'];
const RECEIVED: SentStatus[] = ['waiting', 'reprocessing', 'overdue'];
const DECISION: SentStatus[] = ['partly_paid', 'denied_again'];

/**
 * One sent appeal, in the same card-row shape as Denied/Underpaid (apl-qrow,
 * rcm-18): patient + billed on top, payer·codes below, a status badge with
 * "what's happening" beside it, and a meta line that keeps the sent date,
 * channel and recovered amount the old table carried. Clicking the row opens
 * the read-only record; "Decide" stops propagation so it doesn't also open it.
 */
function RowView({ r, onDecide, onOpen }: { r: AppealSent; onDecide: (r: AppealSent) => void; onOpen: (r: AppealSent) => void }) {
  const badge = BADGE[r.status];
  const metaParts = [`Sent ${r.sentDisplay} · ${r.sentHow}`];
  if (r.recoveredAmount) metaParts.push(`Recovered ${money(r.recoveredAmount)}`);
  const a11y = `${r.patient}, ${money(r.billed)}, ${r.payerShort}, ${r.codes}, ${badge.label}: ${r.happening}`;
  return (
    <CardRow
      selected={false}
      onSelect={() => { onOpen(r); }}
      name={r.patient}
      amount={money(r.billed)}
      sub={`${r.payerShort} · ${r.codes}`}
      tag={badge}
      taglineText={r.happening}
      metaLine={metaParts.join(' · ')}
      a11yLabel={a11y}
      {...(r.action === 'decide' && !isSentDecided(r)
        ? {
            action: (
              <button type="button" className="apl-btn apl-btn-sm" onClick={() => { onDecide(r); }}>
                Decide
              </button>
            ),
          }
        : {})}
    />
  );
}

// ---- The read-only appeal view (§5.6, D131) ----

/** Proof that the appeal went out (always kept). */
function proofOfSending(r: AppealSent): string {
  const how = r.sentHow.toLowerCase();
  if (how.includes('certified')) return `Certified mail receipt kept · sent ${r.sentDisplay}.`;
  if (how.includes('fax')) return `Fax transmission report, all pages · sent ${r.sentDisplay}.`;
  if (how.includes('website') || how.includes('tools')) return `Upload confirmation from ${r.payerShort}’s site, saved ${r.sentDisplay}.`;
  if (how.includes('email')) return `Secure-email send confirmation, saved ${r.sentDisplay}.`;
  return `Send confirmation saved ${r.sentDisplay}.`;
}

/** Proof that the payer received it — shown separately, and only when confirmed. */
function proofOfReceipt(r: AppealSent): { confirmed: boolean; text: string } {
  if (r.status === 'paid') return { confirmed: true, text: `${r.payerShort} confirmed receipt and has since paid.` };
  if (RECEIVED.includes(r.status)) return { confirmed: true, text: `${r.payerShort} confirmed it has the appeal. See the status below.` };
  if (r.status === 'returned') return { confirmed: false, text: `Returned undelivered — ELVA checked the address and sent it again.` };
  if (r.status === 'didnt_go') return { confirmed: false, text: `The first attempt didn’t go through — ELVA sent it another way.` };
  return { confirmed: false, text: `Not confirmed yet — ${r.payerShort} hasn’t acknowledged receipt. ELVA keeps checking.` };
}

/** A short, honest status history for the appeal. */
function timeline(r: AppealSent): { label: string; done: boolean }[] {
  const steps: { label: string; done: boolean }[] = [
    { label: `Prepared by ELVA`, done: true },
    { label: `Sent ${r.sentDisplay} · ${r.sentHow}`, done: true },
  ];
  const receipt = proofOfReceipt(r);
  steps.push({ label: receipt.confirmed ? `${r.payerShort} confirmed receipt` : `Waiting for ${r.payerShort} to confirm receipt`, done: receipt.confirmed });
  if (r.status === 'paid') steps.push({ label: `Paid${r.recoveredAmount ? ` — ${money(r.recoveredAmount)}` : ''}`, done: true });
  else if (DECISION.includes(r.status)) steps.push({ label: `${r.payerShort} answered — needs your decision`, done: true });
  else steps.push({ label: `Waiting for ${r.payerShort}’s answer`, done: false });
  return steps;
}

function AppealSentDetail({ r, onBack }: { r: AppealSent; onBack: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => { ref.current?.focus(); }, []);
  const receipt = proofOfReceipt(r);
  const badge = BADGE[r.status];
  const steps = timeline(r);
  const who = `${APPEALS_USER.name} (${APPEALS_USER.org})`;
  return (
    <div className="apl-sent-page">
      <button type="button" className="apl-btn apl-btn-sm" onClick={onBack} style={{ alignSelf: 'flex-start' }}>
        ← All appeals
      </button>

      <section className="apl-card">
        <div className="apl-card-head" style={{ marginBottom: 6 }}>
          <div ref={ref} tabIndex={-1}>
            <h2 className="apl-h2">{r.patient}</h2>
            <p className="apl-meta num">{r.payerShort} · {r.codes} · {money(r.billed)}</p>
          </div>
          <span className={`apl-badge ${badge.cls}`}>{badge.label}</span>
        </div>
        <p style={{ margin: 0 }}>{r.happening}</p>
        <p className="apl-meta" style={{ margin: '6px 0 0' }}>Read-only. The appeal went as ELVA prepared it; follow-ups appear here as {r.payerShort} responds.</p>
      </section>

      <section className="apl-card">
        <h3 className="apl-h3" style={{ marginTop: 0 }}>How and when it was sent</h3>
        <dl className="apl-dl">
          <dt>Sent</dt><dd>{r.sentDisplay} · {r.sentHow}</dd>
          <dt>Confirmed by</dt><dd>{who}</dd>
          <dt>The packet</dt><dd>Sent to {r.payerShort} exactly as ELVA prepared it.</dd>
        </dl>
      </section>

      {/* Proof of sending and proof of receipt, shown separately (D131). */}
      <section className="apl-card">
        <h3 className="apl-h3" style={{ marginTop: 0 }}>Proof</h3>
        <div className="apl-inner" style={{ marginBottom: 10 }}>
          <div style={{ fontWeight: 600 }}>Proof of sending</div>
          <p style={{ margin: '4px 0 0', color: 'var(--fg2)' }}>{proofOfSending(r)}</p>
        </div>
        <div className="apl-inner">
          <div style={{ fontWeight: 600, display: 'flex', gap: 8, alignItems: 'center' }}>
            {receipt.confirmed ? <span className="apl-mark ok" aria-hidden="true"><Check /></span> : null}
            Proof of receipt
          </div>
          <p style={{ margin: '4px 0 0', color: 'var(--fg2)' }}>{receipt.text}</p>
        </div>
      </section>

      <section className="apl-card">
        <h3 className="apl-h3" style={{ marginTop: 0 }}>Status history</h3>
        <ul className="apl-steps">
          {steps.map((s, i) => (
            <li className="apl-step" key={`${s.label}-${String(i)}`}>
              <span className={s.done ? 'apl-mark ok' : 'apl-step-n'} aria-hidden="true">{s.done ? <Check /> : String(i + 1)}</span>
              <div style={{ color: s.done ? 'inherit' : 'var(--fg2)' }}>{s.label}</div>
            </li>
          ))}
        </ul>
        <p className="apl-meta" style={{ marginTop: 10 }}>
          Retries and follow-ups: ELVA follows up with {r.payerShort} until it answers, and re-sends through another way the payer accepts if the first doesn’t go through — never the same appeal twice.
        </p>
      </section>
    </div>
  );
}

export function AppealsSent({ rows, openId, onDecide }: { rows: AppealSent[]; openId: string | null; onDecide: (r: AppealSent) => void }) {
  const navigate = useNavigate();
  // The open appeal lives in the URL (/denied/appeals/:id, D202) so the browser back button returns to the list.
  const open = openId ? (rows.find((r) => r.id === openId) ?? null) : null;
  const setOpen = (r: AppealSent) => { void navigate(`/denied/appeals/${r.id}`); };

  // Closed rows (decided or marked closed) now live on the "Closed" pill (rcm-18),
  // merged there with the Denied/Underpaid resolved history — not shown inline here.
  const active = rows.filter((r) => !((r.closed ?? false) || isSentDecided(r)));

  const notConfirmed = active.filter((r) => NOT_CONFIRMED.includes(r.status));
  const received = active.filter((r) => RECEIVED.includes(r.status));
  const needsDecision = active.filter((r) => DECISION.includes(r.status));
  const recovered = rows.reduce((s, r) => s + (r.recoveredAmount ?? 0), 0);

  const earliest = rows.reduce((min, r) => (r.sentDate < min ? r.sentDate : min), rows[0]?.sentDate ?? '2026-01-01');
  const month = MONTHS[Number(earliest.slice(5, 7)) - 1] ?? '';

  active.sort((a, b) => {
    const ad = DECISION.includes(a.status) ? 0 : 1;
    const bd = DECISION.includes(b.status) ? 0 : 1;
    if (ad !== bd) return ad - bd;
    return b.sentDate.localeCompare(a.sentDate);
  });

  if (openId && !open) {
    return <NotFoundInPage thing="appeal" id={openId} listLabel="appeals sent" listTo="/denied/appeals" />;
  }

  if (rows.length === 0) {
    return (
      <div className="apl-sent-page">
        <div className="apl-empty">
          <p className="apl-empty-title">No appeals sent yet.</p>
        </div>
      </div>
    );
  }

  // The read-only view replaces the list (§5.6) — opened from a patient row, or
  // straight from /denied/appeals/:id (D202).
  if (open) {
    return <AppealSentDetail r={open} onBack={() => { void navigate('/denied/appeals'); }} />;
  }

  return (
    <div className="apl-sent-page">
      <section className="apl-card apl-summary" aria-label="Appeals so far">
        <div>
          <div className="apl-sum-label">Sent, not confirmed yet</div>
          <div className="apl-sum-value num">{notConfirmed.length} · {money(notConfirmed.reduce((s, r) => s + r.billed, 0))}</div>
        </div>
        <div>
          <div className="apl-sum-label">Payer has it, waiting</div>
          <div className="apl-sum-value num">{received.length} · {money(received.reduce((s, r) => s + r.billed, 0))}</div>
        </div>
        <div>
          <div className="apl-sum-label">Recovered from appeals since {month}</div>
          <div className="apl-sum-value num">{money(recovered)}</div>
        </div>
        <div>
          <div className="apl-sum-label">Needs a decision</div>
          <div className="apl-sum-value num">{needsDecision.length} · {money(needsDecision.reduce((s, r) => s + r.billed, 0))}</div>
        </div>
      </section>

      <section className="apl-card" aria-labelledby="apl-sent-title">
        <div className="apl-card-head">
          <div>
            <h2 className="apl-h2" id="apl-sent-title">Appeals sent</h2>
            <span className="apl-meta">ELVA sends each appeal the way the payer accepts, follows up until the payer answers, and posts the payment when it comes.</span>
          </div>
        </div>
        <div className="apl-qlist">
          {active.map((r) => (
            <RowView key={r.id} r={r} onDecide={onDecide} onOpen={setOpen} />
          ))}
        </div>
      </section>

      <div className="apl-meta" style={{ padding: '0 4px' }}>
        <strong style={{ color: 'var(--fg2)' }}>Faxed</strong> and <strong style={{ color: 'var(--fg2)' }}>Sent</strong> only mean it went out.
        ELVA counts an appeal as received only when the payer confirms it or signs for it.
      </div>
    </div>
  );
}
